// /app/frontend/src/components/MfaChallenge.js
import React, { useState, useEffect } from 'react';
import { ShieldCheck, Loader2, LogOut, KeyRound, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';

const MfaChallenge = () => {
  const { signOut } = useAuth();
  const [factorId, setFactorId] = useState(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  // Modo código de respaldo
  const [backupMode, setBackupMode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    supabase.auth.mfa.listFactors().then(({ data, error: err }) => {
      if (err) {
        setError('No se pudieron cargar tus factores de seguridad. Vuelve a iniciar sesión.');
        return;
      }
      const verified = (data?.totp || []).find((f) => f.status === 'verified');
      if (verified) {
        setFactorId(verified.id);
      } else {
        setError('No se encontró un factor TOTP verificado en tu cuenta.');
      }
    });
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!factorId || code.length !== 6) return;
    setVerifying(true);
    setError('');
    try {
      const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId });
      if (chErr || !challenge) {
        setError(chErr?.message || 'No se pudo iniciar la verificación. Intenta de nuevo.');
        setVerifying(false);
        return;
      }
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (vErr) {
        setError('Código incorrecto o expirado. Verifica tu app e intenta de nuevo.');
        setCode('');
        setVerifying(false);
        return;
      }
      toast.success('Verificación exitosa. ¡Bienvenido!');
      // AuthContext detecta la nueva sesión aal2 y desbloquea la app
    } catch (err) {
      setError('Error inesperado al verificar. Intenta de nuevo.');
      setVerifying(false);
    }
  };

  const handleRedeemBackup = async (e) => {
    e.preventDefault();
    const normalized = backupCode.trim();
    if (normalized.length < 8) return;
    setRedeeming(true);
    setError('');
    try {
      await api.post('/mfa/backup-codes/redeem', { code: normalized });
      toast.success('Código aceptado. El 2FA fue eliminado de tu cuenta — vuelve a activarlo desde Configuración.', { duration: 8000 });
      // Refrescar sesión: el nuevo token ya no requiere MFA y desbloquea la app
      await supabase.auth.refreshSession();
    } catch (err) {
      setError(err.response?.data?.detail || 'Código de respaldo inválido o ya utilizado.');
      setBackupCode('');
      setRedeeming(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-inmoblum-peach/20 via-white to-inmoblum-accent/10 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0" data-testid="mfa-challenge-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center mb-3">
            {backupMode ? (
              <KeyRound className="w-7 h-7 text-indigo-600" />
            ) : (
              <ShieldCheck className="w-7 h-7 text-indigo-600" />
            )}
          </div>
          <h1 className="text-xl font-bold text-slate-900">
            {backupMode ? 'Recuperar con código de respaldo' : 'Verificación en dos pasos'}
          </h1>
          <CardDescription className="text-slate-500">
            {backupMode
              ? 'Ingresa uno de los códigos de respaldo que guardaste al activar el 2FA. El 2FA se eliminará de tu cuenta para que puedas volver a configurarlo.'
              : 'Ingresa el código de 6 dígitos de tu aplicación de autenticación (Google Authenticator, Authy, etc.)'}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          {backupMode ? (
            <form onSubmit={handleRedeemBackup} className="space-y-4">
              <div>
                <Label htmlFor="backup-code">Código de respaldo</Label>
                <Input
                  id="backup-code"
                  type="text"
                  placeholder="XXXX-XXXX"
                  value={backupCode}
                  onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                  className="mt-1.5 text-center text-xl tracking-[0.2em] font-mono h-12"
                  data-testid="mfa-backup-code-input"
                  autoFocus
                  disabled={redeeming}
                />
              </div>

              {error && (
                <p className="text-sm text-red-600" data-testid="mfa-error-message">{error}</p>
              )}

              <Button
                type="submit"
                className="w-full bg-inmoblum-accent hover:bg-inmoblum-accent-dark h-11"
                disabled={redeeming || backupCode.trim().length < 8}
                data-testid="mfa-redeem-backup-btn"
              >
                {redeeming ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Verificando...
                  </>
                ) : (
                  'Recuperar acceso'
                )}
              </Button>

              <button
                type="button"
                onClick={() => { setBackupMode(false); setError(''); }}
                className="w-full inline-flex items-center justify-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700"
                data-testid="mfa-use-totp-link"
              >
                <Smartphone className="w-4 h-4" />
                Usar código de mi app de autenticación
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <Label htmlFor="mfa-code">Código de verificación</Label>
                <Input
                  id="mfa-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="mt-1.5 text-center text-2xl tracking-[0.5em] font-mono h-14"
                  data-testid="mfa-code-input"
                  autoFocus
                  disabled={verifying || !factorId}
                />
              </div>

              {error && (
                <p className="text-sm text-red-600" data-testid="mfa-error-message">{error}</p>
              )}

              <Button
                type="submit"
                className="w-full bg-inmoblum-accent hover:bg-inmoblum-accent-dark h-11"
                disabled={verifying || code.length !== 6 || !factorId}
                data-testid="mfa-verify-btn"
              >
                {verifying ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Verificando...
                  </>
                ) : (
                  'Verificar'
                )}
              </Button>

              <button
                type="button"
                onClick={() => { setBackupMode(true); setError(''); }}
                className="w-full inline-flex items-center justify-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700"
                data-testid="mfa-use-backup-link"
              >
                <KeyRound className="w-4 h-4" />
                ¿Perdiste tu dispositivo? Usa un código de respaldo
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={signOut}
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
              data-testid="mfa-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Cancelar y cerrar sesión
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MfaChallenge;
